// Thin client for the facility-scoped League endpoints (backend
// `app/routers/league.py`, prefix `/leagues`; service `modules/league`). A
// League is the top-level recurring-play shell at a facility; seasons,
// divisions, and matches hang off it in later slices. This Slice-1 client
// covers the list + create + read shell only — there is NO delete/archive
// endpoint in this slice, so the UI ships none.
//
// Types mirror the backend Pydantic models EXACTLY (generated LeagueResponse /
// LeagueCreate). League LISTS are BARE arrays (GET /leagues/facilities/{id}
// returns LeagueResponse[]), NOT a {items, total} envelope — unlike programs.

import { httpClient, userApi } from "@bw/shared/api"
import type {
  LeagueResponse,
  LeagueCreate,
  LeagueUpdate,
  LeagueSeasonResponse,
  LeagueSeasonCreate,
  LeagueDivisionResponse,
  LeagueDivisionCreate,
  LeagueRegistrationResponse,
  LeagueSessionResponse,
  LeagueSessionCreate,
  LeagueStandingResponse,
  LeagueMatchResponse,
  LeagueMatchCreate,
  LeagueMatchResultReport,
  LeagueSessionRegistrationResponse,
} from "@bw/shared/types/generated"

// Re-export the generated types so component code imports them from the thin
// client (single seam), not directly from @bw/shared/types.
export type {
  LeagueResponse,
  LeagueCreate,
  LeagueUpdate,
  LeagueSeasonResponse,
  LeagueSeasonCreate,
  LeagueDivisionResponse,
  LeagueDivisionCreate,
  LeagueRegistrationResponse,
  LeagueSessionResponse,
  LeagueSessionCreate,
  LeagueStandingResponse,
  LeagueMatchResponse,
  LeagueMatchCreate,
  LeagueMatchResultReport,
  LeagueSessionRegistrationResponse,
}

// ---------------------------------------------------------------------------
// Enums (Literal unions mirroring backend Pydantic Literal types)
// ---------------------------------------------------------------------------

// LeagueCreate.format / LeagueResponse.format. The THREE shipped formats — the
// deprecated round_robin/swiss/ladder values are NOT part of this contract.
export type LeagueFormat = "rotating_doubles" | "fixed_doubles" | "singles"

export const LEAGUE_FORMATS: LeagueFormat[] = [
  "rotating_doubles",
  "fixed_doubles",
  "singles",
]

// LeagueResponse.status. Create always yields "active"; there is no delete, so
// "archived" is reached through a lifecycle action that does not exist yet.
export type LeagueStatus = "active" | "archived"

// Division tier bounds (LeagueDivisionCreate.min_tier / max_tier,
// LeagueDivisionResponse.min_tier / max_tier). The SEVEN real player tiers in
// ascending order — the create dialog's Selects map over this and the
// min<=max range guard compares by INDEX into this array. A bound may also be
// NULL (the "Any" sentinel in the UI): an unbounded division accepts any tier.
export type LeagueTier =
  | "iron"
  | "bronze"
  | "silver"
  | "gold"
  | "platinum"
  | "diamond"
  | "grandmaster"

export const LEAGUE_TIERS: LeagueTier[] = [
  "iron",
  "bronze",
  "silver",
  "gold",
  "platinum",
  "diamond",
  "grandmaster",
]

// ---------------------------------------------------------------------------
// League CRUD (list + create + read shell)
// ---------------------------------------------------------------------------

// GET /leagues/facilities/{facility_id} — BARE array of LeagueResponse. The
// optional status filter is appended ONLY when a real enum value is passed; a
// undefined status sends no param (the backend has no UI-only "all" sentinel).
export function listLeagues(
  facilityId: string,
  status?: LeagueStatus,
): Promise<LeagueResponse[]> {
  const suffix = status ? `?status=${status}` : ""
  return httpClient.get<LeagueResponse[]>(
    `/leagues/facilities/${facilityId}${suffix}`,
  )
}

// POST /leagues/facilities/{facility_id} — body LeagueCreate { name, format,
// description? }. Returns the created league in "active" status.
export function createLeague(
  facilityId: string,
  body: LeagueCreate,
): Promise<LeagueResponse> {
  return httpClient.post<LeagueResponse>(
    `/leagues/facilities/${facilityId}`,
    body,
  )
}

// GET /leagues/{league_id} — single league shell.
export function getLeague(leagueId: string): Promise<LeagueResponse> {
  return httpClient.get<LeagueResponse>(`/leagues/${leagueId}`)
}

// PATCH /leagues/{league_id} — body LeagueUpdate { name?, description?, format?,
// status? }. Used both for the edit dialog (name/description/format) and the
// archive/unarchive toggle (status ∈ {"active","archived"}). Returns the updated
// league. Any field not present in `body` is left untouched server-side.
export function updateLeague(
  leagueId: string,
  body: LeagueUpdate,
): Promise<LeagueResponse> {
  return httpClient.patch<LeagueResponse>(`/leagues/${leagueId}`, body)
}

// GET /leagues/{league_id}/seasons — BARE array of LeagueSeasonResponse (NOT an
// {items, total} envelope). Ordering is the component's concern (start_date desc).
export function getSeasons(leagueId: string): Promise<LeagueSeasonResponse[]> {
  return httpClient.get<LeagueSeasonResponse[]>(`/leagues/${leagueId}/seasons`)
}

// POST /leagues/{league_id}/seasons — body LeagueSeasonCreate { name, start_date,
// end_date }. The COMPONENT passes bare `<input type="date">` strings
// ("2026-09-01"); the backend's start_date/end_date are date-time fields, so we
// widen each date string to a UTC-midnight ISO instant HERE — keeping the
// component↔client boundary as plain date strings. A value that already carries a
// time component (or is empty) is passed through untouched.
export function createSeason(
  leagueId: string,
  body: LeagueSeasonCreate,
): Promise<LeagueSeasonResponse> {
  return httpClient.post<LeagueSeasonResponse>(`/leagues/${leagueId}/seasons`, {
    ...body,
    start_date: dateToIso(body.start_date),
    end_date: dateToIso(body.end_date),
  })
}

// Widen a bare "YYYY-MM-DD" date string to a UTC-midnight ISO datetime
// ("2026-09-01" -> "2026-09-01T00:00:00.000Z") so the backend's date-time field
// parses cleanly without timezone drift. Anything that isn't a bare date (already
// has a time, or is empty/garbage) is returned verbatim — the boundary stays honest.
//
// The regex gates on SHAPE only, so a calendar-INVALID day like "2026-02-30"
// still matches; `new Date("2026-02-30T00:00:00.000Z")` then ROLLS OVER to Mar 2,
// silently corrupting the day on the wire. Guard against that: after building the
// Date, verify its UTC y/m/d round-trip the input. If they don't (rollover), return
// the ORIGINAL value unchanged so the backend rejects it honestly — we never emit a
// different day than the operator typed. (A truly unparseable bare date like
// "9999-99-99" yields an Invalid Date whose toISOString() throws upstream — caught
// by the caller's try/catch → toast.error, no corrupt POST.)
function dateToIso(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const [y, m, day] = value.split("-").map(Number)
  const d = new Date(`${value}T00:00:00.000Z`)
  if (
    d.getUTCFullYear() !== y ||
    d.getUTCMonth() + 1 !== m ||
    d.getUTCDate() !== day
  ) {
    return value
  }
  return d.toISOString()
}

// ---------------------------------------------------------------------------
// Season detail — read shell, divisions (list + create), registrations (read)
// (F6 Slice 3). The season-detail page reads a single season + its divisions,
// lets an operator create divisions, and reveals each division's registrations
// on demand. Registration WRITE (register/withdraw) is PLAYER self-service —
// there is intentionally NO register/withdraw client here.
// ---------------------------------------------------------------------------

// GET /leagues/seasons/{season_id} — single season shell (name, date range,
// status, parent league_id for the back-link).
export function getSeason(seasonId: string): Promise<LeagueSeasonResponse> {
  return httpClient.get<LeagueSeasonResponse>(`/leagues/seasons/${seasonId}`)
}

// GET /leagues/seasons/{season_id}/divisions — BARE array of
// LeagueDivisionResponse (NOT an {items, total} envelope). Each row carries its
// own registration_count, so the "N / max_players" count never needs a
// per-division registrations fetch.
export function getDivisions(
  seasonId: string,
): Promise<LeagueDivisionResponse[]> {
  return httpClient.get<LeagueDivisionResponse[]>(
    `/leagues/seasons/${seasonId}/divisions`,
  )
}

// POST /leagues/seasons/{season_id}/divisions — body LeagueDivisionCreate
// { name, max_players (default 16), min_tier?, max_tier? }. RBAC facility:league.
// The COMPONENT passes a {name, max_players, min_tier, max_tier} shape where the
// tier bounds are a real tier OR null (the "Any" sentinel). We NORMALISE null
// bounds away here so an "Any" tier serialises as an OMITTED field on the wire —
// never a null and never an "any" literal token (the backend has no "any" tier).
export function createDivision(
  seasonId: string,
  body: {
    name: string
    max_players: number
    min_tier?: LeagueTier | null
    max_tier?: LeagueTier | null
  },
): Promise<LeagueDivisionResponse> {
  const payload: LeagueDivisionCreate = {
    name: body.name,
    max_players: body.max_players,
  }
  if (body.min_tier) payload.min_tier = body.min_tier
  if (body.max_tier) payload.max_tier = body.max_tier
  return httpClient.post<LeagueDivisionResponse>(
    `/leagues/seasons/${seasonId}/divisions`,
    payload,
  )
}

// GET /leagues/divisions/{division_id}/registrations — BARE array of
// LeagueRegistrationResponse. READ-ONLY in the business UI: register/withdraw
// are player self-service, so this is the only registration client we ship.
export function getRegistrations(
  divisionId: string,
): Promise<LeagueRegistrationResponse[]> {
  return httpClient.get<LeagueRegistrationResponse[]>(
    `/leagues/divisions/${divisionId}/registrations`,
  )
}

// Resolve a player_id (Firebase UID) -> a real display name via the shared user
// profile client (GET /users/{userId}/profile). A registration row never renders
// a raw UID; this wrapper is the single seam that turns the opaque id into a
// label. Mirrors programsApi.getUserDisplayName EXACTLY: on ANY failure —
// network error, 403/404, or a blank/whitespace display_name — it falls back to
// a TRUNCATED-UID label (first 8 chars). It NEVER fabricates a name and NEVER
// returns the full UID.
export async function getPlayerDisplayName(playerId: string): Promise<string> {
  const fallback = playerId.slice(0, 8)
  try {
    const profile = await userApi.getProfile(playerId)
    const name = profile.display_name?.trim()
    return name ? name : fallback
  } catch {
    return fallback
  }
}

// ---------------------------------------------------------------------------
// Season detail — sessions (list + create) + per-division standings (read)
// (F6 Slice 4a). Sessions are the scheduled play occurrences of a season; a
// standing is a player's server-COMPUTED win/loss/points tally within a
// division. Standings are read-only here — they are derived from match results
// on the backend, so there is intentionally NO recompute/edit client.
// ---------------------------------------------------------------------------

// GET /leagues/seasons/{season_id}/sessions — BARE array of LeagueSessionResponse
// (NOT an {items, total} envelope). Ordering is the component's concern
// (session_number asc).
export function getSessions(
  seasonId: string,
): Promise<LeagueSessionResponse[]> {
  return httpClient.get<LeagueSessionResponse[]>(
    `/leagues/seasons/${seasonId}/sessions`,
  )
}

// POST /leagues/seasons/{season_id}/sessions — body LeagueSessionCreate
// { session_number (int), scheduled_date (ISO), start_time (ISO), end_time (ISO) }.
// RBAC facility:league. The COMPONENT passes bare control values — a
// `<input type="date">` string for scheduled_date ("2026-07-01") and bare
// `<input type="time">` strings for start_time/end_time ("18:00"). All three of
// the backend fields are date-time instants, so we widen each HERE — keeping the
// component↔client boundary as plain control strings:
//   - scheduled_date -> UTC-midnight ISO of that day (dateToIso).
//   - start_time / end_time -> the scheduled day combined with the wall-clock
//     time as a UTC instant (combineDateTimeToIso). Both anchor to the SAME
//     scheduled_date so the end>start ordering the component already enforced is
//     preserved on the wire. A value already carrying a time (or empty/garbage)
//     passes through untouched so the backend can reject it honestly.
export function createSession(
  seasonId: string,
  body: LeagueSessionCreate,
): Promise<LeagueSessionResponse> {
  const scheduledIso = dateToIso(body.scheduled_date)
  const dayPart = bareDate(body.scheduled_date)
  return httpClient.post<LeagueSessionResponse>(
    `/leagues/seasons/${seasonId}/sessions`,
    {
      ...body,
      scheduled_date: scheduledIso,
      start_time: combineDateTimeToIso(dayPart, body.start_time),
      end_time: combineDateTimeToIso(dayPart, body.end_time),
    },
  )
}

// GET /leagues/divisions/{division_id}/standings — BARE array of
// LeagueStandingResponse. READ-ONLY: standings are server-computed from match
// results, so there is no create/update client.
export function getStandings(
  divisionId: string,
): Promise<LeagueStandingResponse[]> {
  return httpClient.get<LeagueStandingResponse[]>(
    `/leagues/divisions/${divisionId}/standings`,
  )
}

// ---------------------------------------------------------------------------
// Session matches — matchup generator + result entry (F6 Slice 4b, the FINAL
// Leagues slice). A match is a head-to-head between Team 1 and Team 2 within a
// session; the operator generates matchups from the session's registered player
// pool and reports results. Player REGISTRATION into a session is player
// self-service, so the registrations read below is the READ-ONLY matchmaking
// pool — there is intentionally NO register/withdraw client for it.
// ---------------------------------------------------------------------------

// GET /leagues/sessions/{session_id}/matches — BARE array of LeagueMatchResponse
// (NOT an {items, total} envelope). Ordering is the component's concern
// (created_at asc).
export function getSessionMatches(
  sessionId: string,
): Promise<LeagueMatchResponse[]> {
  return httpClient.get<LeagueMatchResponse[]>(
    `/leagues/sessions/${sessionId}/matches`,
  )
}

// POST /leagues/sessions/{session_id}/matches — body LeagueMatchCreate
// { court_id?, stage (int, default 1), team1_player_ids[], team2_player_ids[] }.
// RBAC facility:league. Court assignment is OUT OF SCOPE this slice — there is no
// court picker in the matchup generator, so court_id is OMITTED on the wire (the
// backend field is optional). The component passes only the team rosters + stage.
export function createMatch(
  sessionId: string,
  body: {
    stage: number
    team1_player_ids: string[]
    team2_player_ids: string[]
    court_id?: string | null
  },
): Promise<LeagueMatchResponse> {
  const payload: LeagueMatchCreate = {
    stage: body.stage,
    team1_player_ids: body.team1_player_ids,
    team2_player_ids: body.team2_player_ids,
  }
  // court_id is deliberately NOT forwarded — no court picker this slice.
  return httpClient.post<LeagueMatchResponse>(
    `/leagues/sessions/${sessionId}/matches`,
    payload,
  )
}

// POST /leagues/matches/{match_id}/result — body LeagueMatchResultReport
// { score (string, e.g. "21-15, 21-18"), winner_team (1 | 2) }. RBAC
// facility:league. Returns the now-completed match with its score + winner.
export function reportMatchResult(
  matchId: string,
  body: LeagueMatchResultReport,
): Promise<LeagueMatchResponse> {
  return httpClient.post<LeagueMatchResponse>(
    `/leagues/matches/${matchId}/result`,
    body,
  )
}

// GET /leagues/sessions/{session_id}/registrations — BARE array of
// LeagueSessionRegistrationResponse. The READ-ONLY player pool for matchmaking:
// players self-register into a session, so there is NO register/withdraw client
// here — the business UI only consumes the pool to build team rosters.
export function getSessionRegistrations(
  sessionId: string,
): Promise<LeagueSessionRegistrationResponse[]> {
  return httpClient.get<LeagueSessionRegistrationResponse[]>(
    `/leagues/sessions/${sessionId}/registrations`,
  )
}

// The bare "YYYY-MM-DD" prefix of a date control value, or null if the value is
// not a bare/leading date (so combineDateTimeToIso passes the time through).
function bareDate(value: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value)
  return m ? m[1] : null
}

// Combine a bare "YYYY-MM-DD" day with a bare "HH:MM" (or "HH:MM:SS") wall-clock
// time into a UTC ISO instant ("2026-07-01" + "18:00" ->
// "2026-07-01T18:00:00.000Z"). The time is interpreted as UTC (consistent with
// dateToIso pinning UTC midnight), so the day never drifts and the end>start
// ordering the component enforced holds on the wire. If either part isn't the
// expected bare shape — or the combined instant rolls the day over — the
// ORIGINAL time value is returned verbatim so the backend rejects it honestly,
// never silently emitting a different instant than the operator typed.
function combineDateTimeToIso(
  day: string | null,
  time: string,
): string {
  if (!day) return time
  const tm = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time)
  if (!tm) return time
  const [hh, mm, ss = "00"] = [tm[1], tm[2], tm[3]]
  const [y, mo, dd] = day.split("-").map(Number)
  const d = new Date(`${day}T${hh}:${mm}:${ss}.000Z`)
  if (Number.isNaN(d.getTime())) return time
  // Guard BOTH a time-of-day rollover (e.g. "25:00") AND a calendar-invalid day
  // rollover (e.g. "2026-02-30" -> Mar 2): if the built instant's UTC y/m/d or
  // h/m don't round-trip the inputs, bail to the raw value so the wire never
  // carries a day/instant the operator did not type. Mirrors dateToIso's
  // round-trip guard above — without the day check, a Feb-30 scheduled_date
  // would silently relabel the times onto Mar 2 and the three fields would
  // disagree on the day.
  if (
    d.getUTCFullYear() !== y ||
    d.getUTCMonth() + 1 !== mo ||
    d.getUTCDate() !== dd ||
    String(d.getUTCHours()).padStart(2, "0") !== hh ||
    String(d.getUTCMinutes()).padStart(2, "0") !== mm
  ) {
    return time
  }
  return d.toISOString()
}
